/**
 * StickyMobileCta — persistent bottom action bar on mobile only.
 *
 * Shown on landing pages once the user has scrolled past the hero
 * (avoids doubling up with the hero CTAs). Hidden entirely when:
 *   - Viewport is >= md (desktop has the navbar CTA always visible)
 *   - The user has scrolled less than 300px from top (hero is on screen)
 *   - The user has dismissed the bar for this session
 *
 * Sits at bottom-0, respects safe-area-inset-bottom for iOS notch,
 * and floats above the AI Sensy WhatsApp widget (which uses z-40 by
 * default). Border-top + backdrop-blur so text underneath stays legible.
 */
import { useEffect, useState } from "react";
import { ArrowRight, Sparkles, X } from "lucide-react";

const DISMISS_KEY = "ac_sticky_cta_dismissed_v1";
const SHOW_AFTER_PX = 300;

export default function StickyMobileCta({ onSignup, onHowItWorks }) {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      if (sessionStorage.getItem(DISMISS_KEY) === "1") { setDismissed(true); return; }
    } catch (_) { /* strict privacy modes */ }

    const onScroll = () => {
      setVisible((window.scrollY || document.documentElement.scrollTop || 0) > SHOW_AFTER_PX);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (dismissed || !visible) return null;

  const dismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch (_) { /* noop */ }
  };

  return (
    <div
      className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur-md"
      style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0.5rem)" }}
      data-testid="sticky-mobile-cta"
    >
      <div className="max-w-lg mx-auto px-3 py-2.5 flex items-center gap-2">
        <button
          type="button"
          onClick={onHowItWorks}
          className="flex-1 h-11 rounded-lg text-[13px] font-semibold text-slate-700 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 transition-colors flex items-center justify-center gap-1.5"
          data-testid="sticky-cta-how"
        >
          <Sparkles className="w-3.5 h-3.5" /> How it works
        </button>
        <button
          type="button"
          onClick={onSignup}
          className="flex-[1.4] h-11 rounded-lg text-[13px] font-bold text-slate-900 bg-amber-500 hover:bg-amber-400 transition-colors flex items-center justify-center gap-1.5 shadow-sm"
          data-testid="sticky-cta-register"
        >
          Register free <ArrowRight className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onClick={dismiss}
          className="w-9 h-11 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center"
          aria-label="Dismiss quick actions"
          data-testid="sticky-cta-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
